import React from 'react';
import { Checkbox, Select } from 'antd';
import { AiReceptionistTriggers, RING_COUNT_OPTIONS } from '../../../../domain/models/AiReceptionist';

interface PickupSectionProps {
  assistantName: string;
  userFirstName: string;
  triggers: AiReceptionistTriggers;
  onToggleTrigger: (key: keyof AiReceptionistTriggers) => void;
  ringCount: number;
  onRingCountChange: (value: number) => void;
}

type TriggerGroup = { title: string; subtitle: string; options: { id: keyof AiReceptionistTriggers; title: string; subtitle: string }[] };

// Same cases and order as the mobile settings (receptionistData.ts TRIGGER_GROUPS)
const buildTriggerGroups = (name: string): TriggerGroup[] => [
  {
    title: 'Avant de faire sonner',
    subtitle: `${name} décroche tout de suite si...`,
    options: [
      { id: 'do_not_disturb', title: 'Ne pas déranger', subtitle: 'Ou un mode Concentration du téléphone.' },
      { id: 'unreachable', title: 'Téléphone injoignable', subtitle: 'Éteint, sans réseau ou en mode avion.' },
      { id: 'outside_hours', title: 'En dehors des horaires', subtitle: 'Selon les horaires de disponibilité.' },
      { id: 'vacation', title: 'Pendant les vacances', subtitle: 'Quand le mode vacances est activé' },
    ],
  },
  {
    title: 'Pendant que ça sonne',
    subtitle: `Sinon, le téléphone sonne. ${name} prend le relais si...`,
    options: [
      { id: 'no_answer', title: 'Pas de réponse', subtitle: 'Personne ne décroche à temps.' },
      { id: 'declined', title: 'Appel refusé', subtitle: `${name} prend l’appel au lieu de la messagerie.` },
      { id: 'busy', title: 'Déjà en double appel', subtitle: `Un 3e appel arrive : ${name} le prend, les deux appels en cours continuent.` },
    ],
  },
];

const PickupSection: React.FC<PickupSectionProps> = ({
  assistantName,
  userFirstName,
  triggers,
  onToggleTrigger,
  ringCount,
  onRingCountChange,
}) => {
  const name = assistantName || 'Donna';
  const checkedCount = Object.values(triggers).filter(Boolean).length;

  return (
    <>
      <p className="receptionist-ai-detail-page__section-subtitle">
        Cochez les situations où {name} prend l&apos;appel. Le reste du temps, le téléphone sonne normalement.
      </p>

      <div className="receptionist-ai-detail-page__timeline">
        <div className="receptionist-ai-detail-page__timeline-marker">
          <span className="receptionist-ai-detail-page__timeline-hollow-dot" />
          <span className="receptionist-ai-detail-page__timeline-marker-label">Un appel arrive</span>
        </div>

        {buildTriggerGroups(name).map((group, index) => (
          <div key={group.title} className="receptionist-ai-detail-page__timeline-group">
            <div className="receptionist-ai-detail-page__timeline-group-head">
              <span className="receptionist-ai-detail-page__timeline-number">{index + 1}</span>
              <span className="receptionist-ai-detail-page__timeline-group-title">{group.title}</span>
            </div>
            <p className="receptionist-ai-detail-page__timeline-group-subtitle">{group.subtitle}</p>

            <div className="receptionist-ai-detail-page__trigger-list">
              {group.options.map((option) => (
                <div key={option.id} className="receptionist-ai-detail-page__trigger-row">
                  <Checkbox
                    className="receptionist-ai-detail-page__trigger-check"
                    checked={triggers[option.id]}
                    // The last checked case can't be unchecked
                    disabled={triggers[option.id] && checkedCount === 1}
                    onChange={() => onToggleTrigger(option.id)}
                  >
                    <span className="receptionist-ai-detail-page__motif-title">{option.title}</span>
                    <span className="receptionist-ai-detail-page__motif-subtitle">{option.subtitle}</span>
                  </Checkbox>
                  {option.id === 'no_answer' && triggers.no_answer && (
                    <div className="receptionist-ai-detail-page__trigger-ring">
                      <label htmlFor="receptionist-ai-ring-count">Au bout de</label>
                      <Select
                        id="receptionist-ai-ring-count"
                        value={ringCount}
                        onChange={onRingCountChange}
                        options={RING_COUNT_OPTIONS.map((count) => ({ value: count, label: `${count} sonneries${count === 7 ? ' (recommandé)' : ''}` }))}
                        className="receptionist-ai-detail-page__trigger-ring-select"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="receptionist-ai-detail-page__timeline-marker">
          <span className="receptionist-ai-detail-page__timeline-hollow-dot" />
          <span className="receptionist-ai-detail-page__timeline-marker-label">
            Sinon, l&apos;appel reste avec {userFirstName || 'vous'}
          </span>
        </div>
      </div>
    </>
  );
};

export default PickupSection;
